import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environment/environment';
import {
  CreateOAuthClientPayload,
  CreateOAuthClientResponse,
  OAuthClient,
  RotateOAuthClientSecretResponse,
  UpdateOAuthClientPayload,
} from '../../dto/oauth/oauth-client.dto';

function unwrapRecord(res: unknown): Record<string, unknown> {
  if (!res || typeof res !== 'object') {
    return {};
  }
  const o = res as Record<string, unknown>;
  const inner = o['data'];
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>;
  }
  return o;
}

@Injectable({ providedIn: 'root' })
export class OAuthClientsService {
  private readonly baseUrl = `${environment.apiUrl}/oauth/clients`;

  constructor(private http: HttpClient) {}

  list(): Observable<OAuthClient[]> {
    return this.http.get<unknown>(this.baseUrl).pipe(
      map((res) => {
        const body = unwrapRecord(res);
        const raw = body['clients'];
        if (!Array.isArray(raw)) {
          return [];
        }
        return raw.map((row) => this.normalizeClient(row));
      })
    );
  }

  create(
    payload: CreateOAuthClientPayload
  ): Observable<CreateOAuthClientResponse> {
    return this.http.post<unknown>(this.baseUrl, payload).pipe(
      map((res) => {
        const body = unwrapRecord(res);
        const client = this.normalizeClient(body['client']);
        const secret =
          typeof body['client_secret'] === 'string'
            ? body['client_secret']
            : undefined;
        return { client, client_secret: secret };
      })
    );
  }

  update(
    id: number,
    payload: UpdateOAuthClientPayload
  ): Observable<OAuthClient> {
    return this.http.put<unknown>(`${this.baseUrl}/${id}`, payload).pipe(
      map((res) => {
        const body = unwrapRecord(res);
        return this.normalizeClient(body['client'] ?? body);
      })
    );
  }

  disable(id: number): Observable<void> {
    return this.http.delete<unknown>(`${this.baseUrl}/${id}`).pipe(map(() => undefined));
  }

  rotateSecret(id: number): Observable<RotateOAuthClientSecretResponse> {
    return this.http
      .post<unknown>(`${this.baseUrl}/${id}/rotate-secret`, {})
      .pipe(
        map((res) => {
          const body = unwrapRecord(res);
          return {
            message:
              typeof body['message'] === 'string' ? body['message'] : undefined,
            client_secret: String(body['client_secret'] ?? ''),
          };
        })
      );
  }

  private normalizeClient(raw: unknown): OAuthClient {
    const o = (raw && typeof raw === 'object' ? raw : {}) as Record<
      string,
      unknown
    >;
    const id = Number(o['id']);
    const redirectUris = Array.isArray(o['redirect_uris'])
      ? o['redirect_uris'].map((u) => String(u))
      : [];
    const scopes = Array.isArray(o['allowed_scopes'])
      ? o['allowed_scopes'].map((s) => String(s))
      : undefined;
    return {
      id: Number.isFinite(id) ? id : 0,
      client_id: String(o['client_id'] ?? ''),
      name: String(o['name'] ?? ''),
      enabled: o['enabled'] !== false && o['enabled'] !== 0,
      redirect_uris: redirectUris,
      allowed_scopes: scopes,
      organizations_id:
        o['organizations_id'] != null ? Number(o['organizations_id']) : null,
      product_key:
        o['product_key'] != null && o['product_key'] !== ''
          ? String(o['product_key'])
          : null,
      access_token_ttl_seconds: Number(o['access_token_ttl_seconds'] ?? 0) || undefined,
      authorization_code_ttl_seconds:
        Number(o['authorization_code_ttl_seconds'] ?? 0) || undefined,
      created_at: o['created_at'] != null ? String(o['created_at']) : null,
      updated_at: o['updated_at'] != null ? String(o['updated_at']) : null,
      created_by:
        o['created_by'] != null ? Number(o['created_by']) : null,
      has_client_secret:
        o['has_client_secret'] === true || o['has_client_secret'] === 1,
    };
  }
}
